import { apiClient } from '@/services/authApi';
import { endpoints } from '@/services/endpoints';
import {
  Document,
  DocumentFilter,
  DocumentType,
  UploadProgress,
} from '@/types/document';

// Backend types
interface ApiDocument {
  _id: string;
  name: string;
  type?: string;
  size?: string;
}

interface DocumentsResponse {
  files: ApiDocument[];
}

interface UploadUrlResponse {
  name?: string;
  key: string;
  uploadUrl: string;
  _id?: string;
}

interface DocumentPreviewResponse {
  _id?: string;
  name?: string;
  url: string;
}

function parseSize(sizeStr: string | undefined): number {
  if (!sizeStr) return 0;
  const num = parseFloat(sizeStr);
  if (sizeStr.toUpperCase().includes('KB')) return num * 1024;
  if (sizeStr.toUpperCase().includes('MB')) return num * 1024 * 1024;
  if (sizeStr.toUpperCase().includes('GB')) return num * 1024 * 1024 * 1024;
  return num;
}

function mapApiDocToDocument(
  d: ApiDocument,
  userId?: string,
  userName?: string
): Document {
  return {
    id: d._id,
    userId: userId ?? '',
    userName,
    name: d.name,
    originalName: d.name,
    type: (d.type as DocumentType) ?? 'other',
    status: 'uploaded',
    size: parseSize(d.size),
    mimeType: d.type ?? 'application/octet-stream',
    uploadedAt: new Date().toISOString(),
  };
}

/**
 * GET /api/docs - current user's documents (taxYear required)
 * GET /api/users/{userId}/docs - when filter.userId is set (admin; taxYear required, optional tenantId)
 */
export async function getDocuments(filter: DocumentFilter): Promise<Document[]> {
  let list: Document[];
  if (filter.userId) {
    const params: Record<string, number | string> = { taxYear: filter.taxYear };
    if (filter.tenantId != null && filter.tenantId !== '') params.tenantId = filter.tenantId;
    const response = await apiClient.get<DocumentsResponse>(
      endpoints.adminDocs.listByUser(filter.userId),
      { params }
    );
    const files = response.data?.files ?? [];
    list = files.map((d) => mapApiDocToDocument(d, filter.userId));
  } else {
    const response = await apiClient.get<DocumentsResponse>(endpoints.docs.list, {
      params: { taxYear: filter.taxYear },
    });
    const files = response.data?.files ?? [];
    list = files.map((d) => mapApiDocToDocument(d));
  }
  if (filter.type) {
    list = list.filter((doc) => doc.type === filter.type);
  }
  if (filter.status) {
    list = list.filter((doc) => doc.status === filter.status);
  }
  if (filter.search) {
    const q = filter.search.toLowerCase();
    list = list.filter(
      (doc) =>
        doc.name.toLowerCase().includes(q) ||
        doc.originalName.toLowerCase().includes(q)
    );
  }
  return list;
}

/**
 * Current user's documents (GET /api/docs). taxYear is required.
 */
export async function getUserDocuments(
  _userId: string,
  taxYear: number
): Promise<Document[]> {
  return getDocuments({ taxYear });
}

/**
 * GET /api/users/{userId}/docs - admin: documents for a specific user (supports taxYear + tenantId)
 */
// export async function getDocumentsByUserId(
//   userId: string,
//   userName?: string,
//   options?: { taxYear?: number; tenantId?: string | null }
// ): Promise<Document[]> {
//   return getDocuments({
//     userId,
//     taxYear: options?.taxYear,
//     tenantId: options?.tenantId,
//   });
// }

/**
 * GET /api/docs/{documentId} - get single document (by id); not in spec as GET one, use list and find or preview
 */
export async function getDocumentById(
  id: string,
  taxYear: number
): Promise<Document | null> {
  const list = await getDocuments({ taxYear });
  return list.find((d) => d.id === id) ?? null;
}

/** Shared upload request body builder (taxYear required by UploadDocumentsRequest) */
function buildUploadBody(
  files: File[],
  taxYear: number
): {
  files: { name: string; type: string; size: string }[];
  taxYear: number;
} {
  return {
    files: files.map((f) => ({
      name: f.name,
      type: f.type || 'application/octet-stream',
      size: formatFileSize(f.size),
    })),
    taxYear,
  };
}

interface PutFilesContext {
  userId?: string;
  userName?: string;
  tenantId?: string | null;
  onFileProgress?: (
    fileIndex: number,
    progress: number,
    status: UploadProgress['status']
  ) => void;
}

function resolveDocumentId(urlInfo: UploadUrlResponse): string {
  if (urlInfo._id) return urlInfo._id;
  const parts = urlInfo.key?.split('/') ?? [];
  return parts[parts.length - 1] ?? '';
}

async function confirmDocUpload(
  documentId: string,
  userId?: string,
  tenantId?: string | null
): Promise<void> {
  if (userId) {
    const params =
      tenantId != null && tenantId !== '' ? { tenantId } : undefined;
    await apiClient.post(
      endpoints.adminDocs.confirmUpload(userId, documentId),
      {},
      { params }
    );
    return;
  }
  await apiClient.post(endpoints.docs.confirmUpload(documentId));
}

async function failDocUpload(
  documentId: string,
  userId?: string,
  tenantId?: string | null
): Promise<void> {
  try {
    if (userId) {
      const params =
        tenantId != null && tenantId !== '' ? { tenantId } : undefined;
      await apiClient.post(
        endpoints.adminDocs.failUpload(userId, documentId),
        {},
        { params }
      );
      return;
    }
    await apiClient.post(endpoints.docs.failUpload(documentId));
  } catch {
    // Best-effort cleanup; ignore network errors on fail-upload
  }
}

/**
 * Puts each file to its presigned upload URL, then confirms with the backend.
 * On PUT failure, notifies backend to soft-delete pending metadata.
 */
async function putFilesToUploadUrls(
  urls: UploadUrlResponse[],
  files: File[],
  context: PutFilesContext
): Promise<Document[]> {
  const { userId, userName, tenantId, onFileProgress } = context;
  const uploaded: Document[] = [];

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const urlInfo = urls[i];
    if (!urlInfo?.uploadUrl) {
      onFileProgress?.(i, 0, 'error');
      continue;
    }
    const documentId = resolveDocumentId(urlInfo);
    const contentType = file.type || 'application/octet-stream';
    try {
      onFileProgress?.(i, 0, 'uploading');
      const res = await fetch(urlInfo.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': contentType },
      });
      if (!res.ok) {
        throw new Error(`Upload failed: ${res.status} ${res.statusText}`);
      }
      if (documentId) {
        await confirmDocUpload(documentId, userId, tenantId);
      }
      onFileProgress?.(i, 100, 'success');
      uploaded.push(
        mapApiDocToDocument(
          {
            _id: documentId || urlInfo.key,
            name: urlInfo.name ?? file.name,
            type: contentType,
            size: String(file.size),
          },
          userId,
          userName
        )
      );
    } catch {
      if (documentId) {
        await failDocUpload(documentId, userId, tenantId);
      }
      onFileProgress?.(i, 0, 'error');
    }
  }

  return uploaded;
}

/**
 * POST /api/docs - request presigned URLs, then PUT each file to uploadUrl
 * Body: { files: [{ name, type?, size? }], taxYear: number }
 */
export async function uploadDocuments(
  _userId: string,
  _userName: string,
  files: File[],
  onFileProgress: (
    fileIndex: number,
    progress: number,
    status: UploadProgress['status']
  ) => void,
  taxYear: number
): Promise<Document[]> {
  const body = buildUploadBody(files, taxYear);
  const response = await apiClient.post<UploadUrlResponse[]>(
    endpoints.docs.upload,
    body
  );
  const urls = Array.isArray(response.data) ? response.data : [];
  return putFilesToUploadUrls(urls, files, { onFileProgress });
}

/**
 * Admin: upload documents on behalf of a user.
 * POST /api/users/{userId}/docs – taxYear required in body; optional tenantId query param.
 */
export async function uploadDocumentsOnBehalfOfUser(
  userId: string,
  userName: string,
  files: File[],
  onFileProgress: (
    fileIndex: number,
    progress: number,
    status: UploadProgress['status']
  ) => void,
  options: { taxYear: number; tenantId?: string | null }
): Promise<Document[]> {
  const body = buildUploadBody(files, options.taxYear);
  const params =
    options.tenantId != null && options.tenantId !== ''
      ? { tenantId: options.tenantId }
      : undefined;
  let response: { data?: UploadUrlResponse[] };
  try {
    response = await apiClient.post<UploadUrlResponse[]>(
      endpoints.adminDocs.uploadForUser(userId),
      body,
      { params }
    );
  } catch (err: unknown) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 404 || status === 501) {
      throw new Error(
        'Admin upload on behalf of user is not yet available. Backend integration pending.'
      );
    }
    throw err;
  }
  const urls = Array.isArray(response.data) ? response.data : [];
  return putFilesToUploadUrls(urls, files, {
    userId,
    userName,
    tenantId: options.tenantId,
    onFileProgress,
  });
}

/**
 * DELETE /api/docs/{documentId}
 */
export async function deleteDocument(id: string): Promise<void> {
  await apiClient.delete(endpoints.docs.delete(id));
}

/**
 * Get preview/download URL - use userId (and optional tenantId) for admin context
 */
async function getDocumentDownloadUrl(
  id: string,
  userId?: string,
  tenantId?: string | null
): Promise<string> {
  if (userId) {
    return getDocumentPreviewUrlAdmin(userId, id, tenantId);
  }
  return getDocumentPreviewUrl(id);
}

/**
 * Download document as blob (fetches pre-signed URL then blob)
 * Pass userId when in admin context; pass tenantId for admin cross-tenant.
 */
export async function downloadDocument(
  id: string,
  userId?: string,
  tenantId?: string | null
): Promise<Blob> {
  const url = await getDocumentDownloadUrl(id, userId, tenantId);
  if (!url) throw new Error('Document not found');
  const res = await fetch(url);
  if (!res.ok) throw new Error('Failed to download document');
  return res.blob();
}

/**
 * GET /api/docs/{documentId} - preview URL for current user
 */
export async function getDocumentPreviewUrl(id: string): Promise<string> {
  const response = await apiClient.get<DocumentPreviewResponse>(
    endpoints.docs.getPreview(id)
  );
  return response.data?.url ?? '';
}

/**
 * GET /api/users/{userId}/docs/{documentId}/preview - admin preview URL (supports tenantId)
 */
export async function getDocumentPreviewUrlAdmin(
  userId: string,
  documentId: string,
  tenantId?: string | null
): Promise<string> {
  const params =
    tenantId != null && tenantId !== '' ? { tenantId } : undefined;
  const response = await apiClient.get<DocumentPreviewResponse>(
    endpoints.adminDocs.getPreview(userId, documentId),
    { params }
  );
  return response.data?.url ?? '';
}

export interface DocumentStatistics {
  total: number;
  byType: Record<string, number>;
  totalSize: number;
}

/**
 * Statistics derived from current user's documents (API has no stats endpoint).
 * taxYear is required to scope the list.
 */
export async function getDocumentStatistics(
  _userId: string | undefined,
  taxYear: number
): Promise<DocumentStatistics> {
  const list = await getDocuments({ taxYear });
  const byType: Record<string, number> = {};
  let totalSize = 0;
  for (const d of list) {
    byType[d.type] = (byType[d.type] ?? 0) + 1;
    totalSize += d.size;
  }
  return {
    total: list.length,
    byType,
    totalSize,
  };
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}
